/**
 * Phone drawer snap geometry, both orientations.
 *
 * Pure helpers, kept out of the component so the drag/snap rules can be unit tested
 * without a touch harness.
 */

import { DRAWER_LAYOUT } from "./layout";

/**
 * Drawer resting places, plus `"collapsed"` — a peek sized to the drawer chrome and the
 * content's title row. Portrait uses the full ladder as percentages of the viewport;
 * landscape uses only `"collapsed"` and `100` (fully open).
 *
 * Dragging down bottoms out at the peek instead of dismissing: a drawer keeps its content
 * mounted when collapsed, so disappearing entirely left no cue that it was still active.
 */
export type SnapPoint = "collapsed" | 50 | 75 | 100;

/** Peek height used until the chrome can be measured (first paint, or a non-layout environment). */
export const COLLAPSED_HEIGHT_FALLBACK = DRAWER_LAYOUT.DRAG_HANDLE_HEIGHT + DRAWER_LAYOUT.HEADER_MIN_HEIGHT;

/**
 * Collapsed peek height in px.
 *
 * `chromeHeightPx` is the drawer's own handle-plus-header strip. `contentHeaderPx` is the
 * content's header, either registered through `DrawerPeekTargetContext` or inferred from
 * the offset of its scroll region, and 0 when neither is available.
 *
 * A drawer rendering its own header needs no content peek — the chrome already carries the
 * title and close button. When the content owns the header and neither source produced a
 * height, fall back to a fixed sliver: showing the top of the content is wrong by a few px,
 * whereas showing none of it hides the panel's identity entirely.
 */
export function peekHeight(chromeHeightPx: number, contentHeaderPx: number, hasOwnHeader: boolean): number {
    // The drawer's own chrome already carries the title and close button. A registered
    // or inferred content row on top of that is a leak — `Window` / `SlottedWindow`
    // register their title even when `DrawerComponent` is rendered with `hideHeader=false`.
    if (hasOwnHeader) {
        return chromeHeightPx;
    }
    if (contentHeaderPx > 0) {
        return chromeHeightPx + contentHeaderPx;
    }
    return chromeHeightPx + DRAWER_LAYOUT.UNREGISTERED_CONTENT_PEEK;
}

/**
 * Content header height inferred from how far its scroll region sits below the top of the
 * drawer body — by definition, the non-scrolling chrome above it. Structural rather than
 * semantic: it needs no class contract and no cooperation from the content, and it holds
 * for the `Panel` / `Panel.Body` shape that Procore tool panels render.
 *
 * Keeps the tallest reading rather than the latest, because readings are not equally
 * trustworthy. A panel novoweb does not control need not set `flex-shrink: 0` on its
 * header, and such a header compresses as the drawer runs out of room — by a lot, since a
 * long scroll region alongside it absorbs shrink in proportion to its content. Taking the
 * latest reading would then shrink the peek, compressing the header further, on every
 * pass, until the drawer collapsed to a sliver. Compression only ever reads *smaller* than
 * the natural height, so the tallest reading is the natural one.
 *
 * The cost is that a header which genuinely shrinks keeps its former peek. Callers reset
 * `previousBestPx` when the content is replaced, which is when that would otherwise stick.
 * Clamped to `MAX_CONTENT_PEEK`; returns `previousBestPx` when there is nothing to measure
 * (no scroll region, or one flush with the top of the body).
 */
export function inferredContentHeader(offsetPx: number | null, previousBestPx: number): number {
    if (offsetPx === null || offsetPx <= 0) {
        return previousBestPx;
    }
    return Math.max(previousBestPx, Math.min(offsetPx, DRAWER_LAYOUT.MAX_CONTENT_PEEK));
}

/** Next snap when dragging or swiping down. Bottoms out at the peek — never dismisses. */
export function snapDown(snap: SnapPoint): SnapPoint {
    if (snap === 100) {
        return 75;
    }
    if (snap === 75) {
        return 50;
    }
    return "collapsed";
}

/** Next snap when dragging or swiping up. */
export function snapUp(snap: SnapPoint): SnapPoint {
    if (snap === "collapsed") {
        return 50;
    }
    if (snap === 50) {
        return 75;
    }
    return 100;
}

/**
 * Height (vh) below which a slow-drag release commits to the collapsed peek — the midpoint
 * between the peek and the 50 snap. Also where a registered FAB starts riding the sheet
 * down (see `fabShiftPx`), so the FAB's descent begins exactly when the drag enters
 * collapse territory.
 */
export function collapseBoundaryVh(peekVh: number): number {
    return (peekVh + 50) / 2;
}

/**
 * Nearest snap for a drawer released at `effectiveVh` of viewport height.
 *
 * Midpoints: collapsed↔50 (≈30 on a typical phone), 50↔75 = 62.5, 75↔100 = 87.5.
 */
export function findNearestSnap(effectiveVh: number, peekVh: number): SnapPoint {
    if (effectiveVh < collapseBoundaryVh(peekVh)) {
        return "collapsed";
    }
    if (effectiveVh < 62.5) {
        return 50;
    }
    if (effectiveVh < 87.5) {
        return 75;
    }
    return 100;
}

/**
 * Inline `height` for a snap point. The peek is a measured px value — already inclusive of
 * the container's safe-area padding, so it is the sheet's full extent — and the other snaps
 * are viewport-relative.
 */
export function snapHeight(snap: SnapPoint, collapsedHeightPx: number): string {
    return snap === "collapsed" ? `${collapsedHeightPx}px` : `${snap}dvh`;
}

/**
 * Inline `height` for a landscape snap point. The panel is anchored to the bottom like the
 * portrait sheet and collapses the same way, by height — so the peek is the same measured
 * px, and "open" is everything below the top bar, viewport-relative so it tracks the
 * dynamic viewport.
 */
export function landscapeSnapHeight(snap: SnapPoint, collapsedHeightPx: number, topBarPx: number): string {
    return snap === "collapsed" ? `${collapsedHeightPx}px` : `calc(100dvh - ${topBarPx}px)`;
}

/**
 * Peek height as a vh value so the drag math stays in a single unit. Clamped below the 50
 * snap so an unusually tall header on a short viewport can't invert the snap order.
 */
export function collapsedPeekVh(collapsedHeightPx: number, viewportHeightPx: number): number {
    if (viewportHeightPx <= 0) {
        return 0;
    }
    return Math.min(50, (collapsedHeightPx / viewportHeightPx) * 100);
}

/**
 * How far down (px) a registered FAB should be translated for a drawer showing
 * `visibleHeightPx` of itself.
 *
 * The FAB is viewport-fixed, so left alone it would float over the collapsed peek and
 * obscure the header the peek exists to show. Instead it rides the pull-down: at rest
 * above the collapse boundary it stays home (0); from the boundary down to the peek it
 * descends in proportion to the sheet, reaching `hideDistancePx` — fully off-screen —
 * exactly when the sheet rests at the peek. On a typical phone the boundary-to-peek range
 * is close to `hideDistancePx`, so the FAB tracks the sheet's edge at roughly 1:1, which
 * is what makes it read as anchored to the sheet rather than fading on a timer.
 *
 * Pure px/vh mapping so the anchoring rule is unit-testable. Degenerate geometry (a peek
 * tall enough to reach the boundary) leaves no range to ride: the FAB stays home at the
 * peek — whose header is then nowhere near it — and the below-peek guard keeps the
 * division well-defined.
 */
export function fabShiftPx(
    visibleHeightPx: number,
    collapsedHeightPx: number,
    viewportHeightPx: number,
    hideDistancePx: number,
): number {
    if (viewportHeightPx <= 0) {
        return 0;
    }
    const startPx = (collapseBoundaryVh(collapsedPeekVh(collapsedHeightPx, viewportHeightPx)) / 100) * viewportHeightPx;
    return fabShiftBetween(visibleHeightPx, collapsedHeightPx, startPx, hideDistancePx);
}

/**
 * The FAB mapping above with the collapse boundary supplied directly: home at or above
 * `boundaryPx`, fully hidden at or below `collapsedHeightPx`, linear in between. Landscape
 * has no 50 snap to derive a boundary from, so it passes the midpoint of its own range.
 */
export function fabShiftBetween(
    visibleHeightPx: number,
    collapsedHeightPx: number,
    boundaryPx: number,
    hideDistancePx: number,
): number {
    if (visibleHeightPx >= boundaryPx) {
        return 0;
    }
    if (visibleHeightPx <= collapsedHeightPx || boundaryPx <= collapsedHeightPx) {
        return hideDistancePx;
    }
    const progress = (visibleHeightPx - collapsedHeightPx) / (boundaryPx - collapsedHeightPx);
    return (1 - progress) * hideDistancePx;
}

/**
 * Landscape has two resting places: fully open (`100`) and the collapsed peek. A release
 * below the midpoint of that range collapses; at or above it opens.
 */
export function nearestLandscapeSnap(
    visibleHeightPx: number,
    collapsedHeightPx: number,
    openHeightPx: number,
): SnapPoint {
    return visibleHeightPx < (collapsedHeightPx + openHeightPx) / 2 ? "collapsed" : 100;
}
